import { useCallback, useEffect, useRef, useState } from 'react';
import { listFolderContents } from 'src/services/WorkDocsService';
import { useWorkDocsAuth } from 'src/hooks/useWorkDocsAuth';
import { useIsMounted } from 'src/hooks/useIsMounted';
import { logger } from 'src/logger';
import { OrgChartEmployee } from '../types/orgChart.types';
import { evaluateEmployeeCoverage, EmployeeCoverageResult, findSubfolderIdByName, hasManagerHubXlsxFile } from '../utils/readinessCheckUtils';
import { documentMatchesEmployee } from '../utils/documentMatch';
import { runWithConcurrency } from '../utils/concurrency';
import { BATCH_SIZE } from '../constants/BaseballCardConstants';
import { MANAGER_HUB_EXPORTS_FOLDER, SUPPLEMENTAL_DATA_FOLDER } from '../utils/workDocsFolderPlan';

/** Advisory severity for a single folder check. */
export type CheckStatus = 'success' | 'warning' | 'error' | 'pending';

/**
 * Result of the "Manager Hub Exports" check: a presence test for a `manager-hub*.xlsx` ratings
 * export. No per-employee coverage — the folder holds one team-wide file, so only status + message
 * are surfaced.
 */
export interface ManagerHubCheckResult {
  status: CheckStatus;
  message: string;
}

/** A per-employee coverage check (supplemental) plus its rolled-up status. */
export interface CoverageCheckResult extends EmployeeCoverageResult {
  status: CheckStatus;
}

export interface FolderChecks {
  managerHub: ManagerHubCheckResult;
  supplemental: CoverageCheckResult;
}

interface UseFolderChecksArgs {
  /** Provisioned root folder id, or null if folders aren't set up yet. */
  rootFolderId: string | null;
  /** The employees selected for card generation, used for supplemental coverage matching. */
  selectedEmployees: OrgChartEmployee[];
  /** True when Step 4 (Generate) is the active wizard step — gates the auto-run. */
  isActive: boolean;
}

interface UseFolderChecksReturn {
  checks: FolderChecks | null;
  isRunning: boolean;
  error: string | null;
  run: () => Promise<void>;
}

// Bound the Supplemental Context walk so a deep/wide org can't fan out unboundedly.
const MAX_SUPPLEMENTAL_FOLDERS = 400;

const pendingCoverage = (): CoverageCheckResult => ({ status: 'pending', covered: [], missing: [] });

const coverageStatus = (missing: string[]): CheckStatus => (missing.length === 0 ? 'success' : 'warning');

/**
 * Runs the two advisory Step 4 folder checks against the provisioned WorkDocs tree: a presence
 * test for the Manager Hub ratings export (one `manager-hub*.xlsx`), and per-employee coverage of
 * the Supplemental Context subtree. Lists the root once to resolve the two fixed subfolder ids,
 * then runs both checks concurrently (`Promise.all`) so their network latencies overlap. Advisory
 * only — never blocks generation; failures surface as `error` and each check carries its own
 * status. Auto-runs when Step 4 becomes active and re-arms whenever the selection or root changes.
 *
 * Trimmed from the former four-check readiness flow: only the two surviving upload folders are
 * checked, and the supplemental walk reuses the same bounded, deduped BFS.
 */
export const useFolderChecks = ({ rootFolderId, selectedEmployees, isActive }: UseFolderChecksArgs): UseFolderChecksReturn => {
  const { accessToken } = useWorkDocsAuth();
  const isMounted = useIsMounted();

  const [checks, setChecks] = useState<FolderChecks | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Monotonic run id. The invalidation effect bumps it whenever the inputs change; each `run`
  // snapshots it at start and only commits its results if the snapshot still matches on completion.
  // This closes a race: if the selection changes while a run is in flight, that run's results are
  // for the OLD selection — committing them would overwrite the just-cleared `checks` with stale
  // data AND leave the auto-run's `!checks` guard unsatisfied, so it would never re-fire for the
  // new selection. Discarding the stale write keeps `checks` null, and the unconditional
  // `isRunning=false` in `finally` lets the auto-run re-fire with the current selection.
  const runIdRef = useRef(0);

  const run = useCallback(async () => {
    if (!rootFolderId || !accessToken) {
      setError('Connect WorkDocs and set up folders before checking.');
      return;
    }

    const snapshotId = runIdRef.current;
    setIsRunning(true);
    setError(null);

    try {
      // Resolve the two fixed subfolder ids from a single root listing.
      const { folders: rootFolders } = await listFolderContents(rootFolderId, accessToken);
      const managerHubId = findSubfolderIdByName(rootFolders, MANAGER_HUB_EXPORTS_FOLDER);
      const supplementalId = findSubfolderIdByName(rootFolders, SUPPLEMENTAL_DATA_FOLDER);

      // The two checks are independent once the ids are known — run them together so their
      // network latencies overlap rather than accumulate.
      const [managerHub, supplemental] = await Promise.all([
        evaluateManagerHub(managerHubId, accessToken),
        supplementalId ? evaluateSupplementalCoverage(supplementalId, accessToken, selectedEmployees) : Promise.resolve(pendingCoverage())
      ]);

      // Commit only if still mounted AND this run wasn't superseded by an input change mid-flight.
      if (isMounted.current && snapshotId === runIdRef.current) {
        setChecks({ managerHub, supplemental });
      }
    } catch (err) {
      logger.warn('Baseball Cards: folder checks failed', { error: err instanceof Error ? err.message : String(err) });
      if (isMounted.current && snapshotId === runIdRef.current) setError('Could not check folders. Try again.');
    } finally {
      // Always clear isRunning (even for a superseded run) so the auto-run effect can re-fire for
      // the current selection now that its stale results were discarded.
      if (isMounted.current) setIsRunning(false);
    }
  }, [rootFolderId, accessToken, selectedEmployees, isMounted]);

  // Invalidate cached results whenever the selection (or root folder) changes — the supplemental
  // coverage is derived from `selectedEmployees`, so a Step 2 re-selection makes it stale. Bumping
  // `runIdRef` first makes any in-flight run discard its results (see `run`); clearing `checks`
  // re-arms the auto-run effect below. `selectedEmployees` is a fresh array reference only when the
  // page's memoized selection actually changes, so this does not loop.
  useEffect(() => {
    runIdRef.current += 1;
    setChecks(null);
  }, [selectedEmployees, rootFolderId]);

  // Auto-run when Step 4 is active and no fresh result is cached. Mirrors the former readiness
  // step's auto-run guard (`isActive && !checks && !isRunning`) so the indicators populate without
  // a manual trigger — including after a Step 3 upload (which re-lists folders) then advancing.
  useEffect(() => {
    if (isActive && !checks && !isRunning) {
      void run();
    }
  }, [isActive, checks, isRunning, run]);

  return { checks, isRunning, error, run };
};

/**
 * Manager Hub check: presence-only test for a `manager-hub*.xlsx` export (see
 * {@link hasManagerHubXlsxFile}). Advisory only, so a missing folder or file is a WARNING (never
 * blocks generation). Returns without a network call when the folder id is missing.
 */
async function evaluateManagerHub(managerHubId: string | undefined, accessToken: string): Promise<ManagerHubCheckResult> {
  // A missing folder and a folder with no export both mean the same thing to the user: the export
  // hasn't been uploaded yet. Surface one friendly warning line for both.
  const notUploaded: ManagerHubCheckResult = { status: 'warning', message: 'Not uploaded yet — add your export to this folder' };
  if (!managerHubId) {
    return notUploaded;
  }
  const { documents } = await listFolderContents(managerHubId, accessToken);
  if (hasManagerHubXlsxFile(documents)) {
    return { status: 'success', message: 'Manager Hub ratings file found.' };
  }
  return notUploaded;
}

/** A folder queued for the Supplemental walk: its id plus the name it was listed under. */
interface SupplementalNode {
  id: string;
  name: string;
}

/**
 * Walks the Supplemental Context subtree (bounded) and reports which selected employees have at
 * least one document under their own per-login folder. A folder "belongs" to an employee when its
 * name matches the employee (login or full name); documents directly in that folder count as
 * coverage.
 *
 * The walk is level-by-level with bounded concurrency (siblings within a level are listed in
 * parallel via `runWithConcurrency`) and a `seen` set keyed by folder id, so every folder is listed
 * exactly once. Coverage is attributed when a folder is listed — checking its own name + documents.
 */
async function evaluateSupplementalCoverage(
  supplementalId: string,
  accessToken: string,
  selectedEmployees: OrgChartEmployee[]
): Promise<CoverageCheckResult> {
  const coveredLogins = new Set<string>();
  const seen = new Set<string>([supplementalId]);
  // The root folder itself is never a person folder; seed the walk with an empty name so it
  // can't spuriously match an employee.
  let currentLevel: SupplementalNode[] = [{ id: supplementalId, name: '' }];
  let listedCount = 0;

  while (currentLevel.length > 0 && listedCount < MAX_SUPPLEMENTAL_FOLDERS) {
    // Never list more than the remaining budget, so the hard cap holds even for a wide level.
    const batch = currentLevel.slice(0, MAX_SUPPLEMENTAL_FOLDERS - listedCount);
    listedCount += batch.length;

    const nextLevel: SupplementalNode[] = [];

    await runWithConcurrency(batch, BATCH_SIZE, async (node) => {
      let contents;
      try {
        contents = await listFolderContents(node.id, accessToken);
      } catch (error) {
        logger.warn('Baseball Cards: supplemental walk failed to list a folder', {
          folderId: node.id,
          error: error instanceof Error ? error.message : String(error)
        });
        return;
      }

      // Attribute coverage to this folder if its own name maps to a selected employee and it
      // directly holds any document. (The root's empty name never matches.)
      const matchedEmployee = selectedEmployees.find((employee) => documentMatchesEmployee(node.name, employee));
      if (matchedEmployee && !coveredLogins.has(matchedEmployee.login) && contents.documents.length > 0) {
        coveredLogins.add(matchedEmployee.login);
      }

      // Enqueue unseen child folders for the next level; the `seen` guard prevents any folder
      // from being listed twice.
      for (const child of contents.folders) {
        if (!seen.has(child.id)) {
          seen.add(child.id);
          nextLevel.push({ id: child.id, name: child.name });
        }
      }
    });

    currentLevel = nextLevel;
  }

  const covered: string[] = [];
  const missing: string[] = [];
  for (const employee of selectedEmployees) {
    if (coveredLogins.has(employee.login)) {
      covered.push(employee.login);
    } else {
      missing.push(employee.login);
    }
  }

  // Supplemental docs are optional context — report as success/warning, never an error.
  return { status: coverageStatus(missing), covered, missing };
}
